/**
 * Conversational Lead Form Component for ShopKaBot
 * Adapted from Subject Buddy's GetHelpForm for B2B AI automation
 * Collects leads for AI agent automation inquiries
 */

import { useState, FormEvent } from "react";
import { Box, Button, TextField, Select, MenuItem, Checkbox, FormControlLabel, Stack, Paper, Typography, Alert, CircularProgress } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import "./LeadForm.css";

interface LeadFormData {
  name: string;
  email: string;
  company: string;
  businessType: string;
  currentChallenge: string;
  budget: string;
  agreeToContact: boolean;
}

type LeadFormErrors = Partial<Record<keyof LeadFormData, string>>;
type SubmissionStatus = "idle" | "success" | "loading" | "error";

interface LeadFormProps {
  formSize?: "sm" | "md" | "lg";
  onSuccess?: () => void;
}

const BUSINESS_TYPES = [
  "E-commerce",
  "Retail",
  "Real Estate",
  "Consulting",
  "Healthcare",
  "Hospitality",
  "Service Provider",
  "SaaS",
  "Other"
];

const BUDGET_RANGES = [
  "₹999-5,000",
  "₹5,000-20,000",
  "₹20,000-50,000",
  "₹50,000+",
  "Not sure"
];

function ShopKaBotLeadForm({ formSize = "md", onSuccess }: LeadFormProps) {
  const [formData, setFormData] = useState<LeadFormData>({
    name: "",
    email: "",
    company: "",
    businessType: "",
    currentChallenge: "",
    budget: "",
    agreeToContact: true,
  });

  const [errors, setErrors] = useState<LeadFormErrors>({});
  const [status, setStatus] = useState<SubmissionStatus>("idle");
  const [message, setMessage] = useState("");

  const setFieldValue = <K extends keyof LeadFormData>(
    field: K,
    value: LeadFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = (): boolean => {
    const nextErrors: LeadFormErrors = {};

    if (!formData.name.trim()) {
      nextErrors.name = "Please enter your name";
    }

    if (!formData.email.trim()) {
      nextErrors.email = "Please enter your email";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Please enter a valid email";
    }

    if (!formData.company.trim()) {
      nextErrors.company = "Please enter your company name";
    }

    if (!formData.businessType) {
      nextErrors.businessType = "Please select your business type";
    }

    if (!formData.currentChallenge.trim()) {
      nextErrors.currentChallenge = "Please describe your business challenge";
    } else if (formData.currentChallenge.trim().length < 20) {
      nextErrors.currentChallenge = "Please provide more details (at least 20 characters)";
    }

    if (!formData.budget) {
      nextErrors.budget = "Please select a budget range";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      // Simulate API call - replace with actual backend endpoint
      const response = await fetch("/api/leads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          source: "shopkabot-website",
          timestamp: new Date().toISOString(),
        }),
      });

      if (response.ok) {
        setStatus("success");
        setMessage("Thank you! We'll contact you within 24 hours to discuss your requirements.");
        setFormData({
          name: "",
          email: "",
          company: "",
          businessType: "",
          currentChallenge: "",
          budget: "",
          agreeToContact: true,
        });
        onSuccess?.();
      } else {
        throw new Error("Failed to submit form");
      }
    } catch (error) {
      setStatus("error");
      setMessage(
        "We couldn't process your request. Please try again or contact us directly at support@shopkabot.com"
      );
    }
  };

  const inputSize = formSize === "lg" ? "medium" : "small";

  return (
    <Paper
      className={`lead-form lead-form-${formSize}`}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: 2,
        background: "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
      }}
    >
      <Typography variant="h5" component="h3" sx={{ mb: 1, fontWeight: 700 }}>
        Get Your AI Automation Consultation
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Tell us about your business challenges, and our experts will show you how ShopKaBot can help.
      </Typography>

      {message && (
        <Alert severity={status === "success" ? "success" : "error"} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} className="lead-form-container">
        <Stack spacing={2.5}>
          {/* Name */}
          <TextField
            fullWidth
            label="Your Name"
            size={inputSize}
            value={formData.name}
            onChange={(e) => setFieldValue("name", e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            placeholder="John Smith"
            required
          />

          {/* Email */}
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            size={inputSize}
            value={formData.email}
            onChange={(e) => setFieldValue("email", e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            placeholder="john@company.com"
            required
          />

          {/* Company */}
          <TextField
            fullWidth
            label="Company Name"
            size={inputSize}
            value={formData.company}
            onChange={(e) => setFieldValue("company", e.target.value)}
            error={!!errors.company}
            helperText={errors.company}
            placeholder="Your Company"
            required
          />

          {/* Business Type */}
          <Select
            fullWidth
            value={formData.businessType}
            onChange={(e) => setFieldValue("businessType", e.target.value)}
            error={!!errors.businessType}
            displayEmpty
            size={inputSize}
            sx={{
              ...(errors.businessType && {
                borderColor: "error.main",
                "&.Mui-error": {
                  borderColor: "error.main",
                },
              }),
            }}
          >
            <MenuItem value="" disabled>
              Select Your Business Type
            </MenuItem>
            {BUSINESS_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
          {errors.businessType && (
            <Typography variant="caption" color="error" sx={{ mt: -1.5 }}>
              {errors.businessType}
            </Typography>
          )}

          {/* Current Challenge */}
          <TextField
            fullWidth
            label="What's Your Main Business Challenge?"
            multiline
            rows={3}
            size={inputSize}
            value={formData.currentChallenge}
            onChange={(e) => setFieldValue("currentChallenge", e.target.value)}
            error={!!errors.currentChallenge}
            helperText={errors.currentChallenge}
            placeholder="E.g., We need better customer support response times, or We want to automate our sales follow-ups..."
            required
          />

          {/* Budget */}
          <Select
            fullWidth
            value={formData.budget}
            onChange={(e) => setFieldValue("budget", e.target.value)}
            error={!!errors.budget}
            displayEmpty
            size={inputSize}
          >
            <MenuItem value="" disabled>
              Select Your Budget Range
            </MenuItem>
            {BUDGET_RANGES.map((range) => (
              <MenuItem key={range} value={range}>
                {range}
              </MenuItem>
            ))}
          </Select>
          {errors.budget && (
            <Typography variant="caption" color="error" sx={{ mt: -1.5 }}>
              {errors.budget}
            </Typography>
          )}

          {/* Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.agreeToContact}
                onChange={(e) => setFieldValue("agreeToContact", e.target.checked)}
              />
            }
            label="Yes, you can contact me about ShopKaBot services and updates"
          />

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            size={formSize === "sm" ? "small" : "large"}
            disabled={status === "loading"}
            endIcon={status === "loading" ? <CircularProgress size={20} /> : <SendIcon />}
            sx={{
              mt: 1,
              background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              },
            }}
          >
            {status === "loading" ? "Submitting..." : "Get Free Consultation"}
          </Button>
        </Stack>
      </Box>

      <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: "block" }}>
        We'll reach out within 24 hours. No spam, just genuine automation insights.
      </Typography>
    </Paper>
  );
}

export default ShopKaBotLeadForm;
